import { useCallback, useMemo } from 'react';

import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import {
  applyTotalAmountRange,
  applyVoucherDateRange,
  createFmsFilterChipChange,
  TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
} from '#/modules/finMgmt/shared/helper/fmsListRangeFilters';

import {
  buildVoucherColumns,
  voucherSideMsgBlocks,
} from '../data/voucherColumns';
import { getVoucherApiErrorMessage } from '../helper/apiError';
import { VOUCHER_LIST_SORT_FIELD_LABELS } from '../helper/voucherStatus';
import useVoucherList from '../hooks/useVoucherList';

/**
 * 財務管理：憑證列表（會計審核用）。
 * 新增憑證在個人中心；這裡不放新增鈕。
 */
const VoucherListPage = () => {
  const { displayData, setSelectedData } = useSideDetail();
  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useVoucherList();

  const columns = useMemo(
    () => buildVoucherColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      onTotalAmountRangeChange: (range) => applyTotalAmountRange(setFilter, range),
      onVoucherDateRangeChange: (range) => applyVoucherDateRange(setFilter, range),
      mode: 'finance',
    }),
    [listQuery.filters, setFilter],
  );

  const filterStatusFields = useMemo(
    () => [
      ...buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
      ...TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
    ],
    [columns, listQuery.filters],
  );

  const handleFilterChipChange = useMemo(() => createFmsFilterChipChange(setFilter), [setFilter]);

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    setSelectedData(isSelected ? row.original : null);
  }, [setSelectedData]);

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              tableKey="fms.voucher.finance.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getVoucherApiErrorMessage(error, '載入憑證失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋憑證號碼或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={VOUCHER_LIST_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChipChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>
          <SideMsg
            title="憑證"
            titleValue="voucherNo"
            displayData={displayData}
            blocks={voucherSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default VoucherListPage;
